import React, { forwardRef, useCallback, useEffect, useMemo, useReducer, useRef, useState, memo, } from 'react';
import styled from '@emotion/styled';
import { css, ThemeProvider } from '@emotion/react';
import { AnimatePresence, m as motion } from 'framer-motion';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import { highlight } from '@polestar/component-warehouse-react/tokens/colors/Colors';
import NavigationDrawerList from '@polestar/component-warehouse-react/components/navigation-drawer/internal/NavigationDrawerList';
import { addKeysToNavigationDrawer, aimingDelta, flattenGroupsIntoLinks, getPreviousFocusPath, useMobileMode, } from '@polestar/component-warehouse-react/components/navigation-drawer/NavigationDrawer.helpers';
import ScrollView from '@polestar/component-warehouse-react/components/scroll-view/ScrollView';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import IconButton, { IconButtonVariants, } from '@polestar/component-warehouse-react/components/icon-button/IconButton';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import { createTransition, Transitions, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import SSRNavigation from '@polestar/component-warehouse-react/components/navigation-drawer/internal/SSRNavigationDrawer';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import { useIsNavigationDrawerOpen, useNavigationDrawer, } from '@polestar/component-warehouse-react/components/navigation-drawer/NavigationDrawerContext';
import Portal from '@polestar/component-warehouse-react/components/portal/portal';
import Backdrop, { BackdropVariants, } from '@polestar/component-warehouse-react/components/backdrop/Backdrop';
import LayoutIndexes from '@polestar/component-warehouse-react/tokens/layout-indexes/LayoutIndexes';
import useDirectionStyle from '@polestar/component-warehouse-react/hooks/useDirectionStyle';
import { useModal } from '@polestar/component-warehouse-react/contexts/modal';
import { useFocusTrap } from '@polestar/component-warehouse-react/hooks/focus';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { useReducedMotion } from '@polestar/component-warehouse-react/hooks/useReducedMotion';
import Themes from '@polestar/component-warehouse-react/themes/Themes';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import useTrackingData from '@polestar/component-warehouse-react/hooks/useTrackingData';
import { DataLayerAction, DataLayerCategory, DataLayerLabel, } from '@polestar/component-warehouse-react/tokens/analytics/Analytics';
import warnOnce from '@polestar/component-warehouse-react/helpers/warnOnce';
import { preventCancelableDefault } from '@polestar/component-warehouse-react/helpers/event';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
export var NavigationDrawerGroupTypes;
(function (NavigationDrawerGroupTypes) {
    NavigationDrawerGroupTypes["small"] = "small";
    NavigationDrawerGroupTypes["large"] = "large";
})(NavigationDrawerGroupTypes || (NavigationDrawerGroupTypes = {}));
const COLUMNS_ON_SCREEN = {
    [DeviceTypes.mobile]: 1,
    [DeviceTypes.tablet]: 2,
    [DeviceTypes.desktop]: 3,
    [DeviceTypes.desktopWide]: 3,
};
const navigationReducer = (state, action) => {
    var _a;
    switch (action.type) {
        case 'setColumn': {
            state.delayedTimer && clearTimeout(state.delayedTimer);
            if (state.columns.length >= action.index) {
                const newColumns = state.columns.slice(0, action.index);
                if (action.column) {
                    newColumns[action.index] = {
                        ...action.column,
                        index: action.index,
                    };
                }
                const columnsOnScreen = COLUMNS_ON_SCREEN[state.deviceType];
                const usefulColumns = newColumns.filter((column) => column.links).length;
                return {
                    ...state,
                    usefulColumns,
                    columns: newColumns,
                    columnsOnScreen,
                    navigationIndex: usefulColumns - columnsOnScreen,
                };
            }
            return state;
        }
        case 'removeColumn': {
            state.delayedTimer && clearTimeout(state.delayedTimer);
            const columnsOnScreen = COLUMNS_ON_SCREEN[state.deviceType];
            // only for keyboard to remove 1 column
            const newColumns = state.columns.splice(0, state.columns.length - 1);
            const usefulColumns = newColumns.filter((column) => column.links).length;
            return {
                ...state,
                columns: newColumns,
                usefulColumns,
                navigationIndex: state.mobileMode
                    ? state.navigationIndex - 1
                    : usefulColumns - columnsOnScreen,
            };
        }
        case 'clearColumns': {
            state.delayedTimer && clearTimeout(state.delayedTimer);
            const columnsOnScreen = COLUMNS_ON_SCREEN[state.deviceType];
            return {
                ...state,
                columns: [],
                usefulColumns: 0,
                navigationIndex: state.mobileMode ? state.navigationIndex : 1 - columnsOnScreen,
            };
        }
        case 'setAiming':
            if (state.aiming !== action.aiming) {
                return {
                    ...state,
                    aiming: action.aiming,
                };
            }
            return state;
        case 'setDisplayMode': {
            const columnsOnScreen = COLUMNS_ON_SCREEN[action.deviceType];
            const usefulColumns = state.columns.filter((column) => column.links).length;
            const columnsStillFit = columnsOnScreen > usefulColumns || state.columns.length === 0;
            const newUsefulColumns = columnsStillFit ? state.usefulColumns : 1;
            const newColumns = columnsStillFit ? state.columns : [(_a = state.columns) === null || _a === void 0 ? void 0 : _a[0]];
            return {
                ...state,
                mobileMode: action.mobileMode,
                deviceType: action.deviceType,
                columnsOnScreen,
                usefulColumns: newUsefulColumns,
                columns: newColumns,
                navigationIndex: state.mobileMode
                    ? -1
                    : columnsOnScreen !== state.columnsOnScreen
                        ? 1 - columnsOnScreen
                        : state.navigationIndex,
            };
        }
    }
};
/**
 * We need values rounded to 1 decimal place so we can vertically align the close button
 * correctly with the link arrows below. i.e. No `math.ceil()`.
 */
const round = (value, precision) => {
    const multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
};
const NavigationDrawer = forwardRef(({ id, groups, header, onAnimationEnded, closeButtonProps, backButtonProps, internal = false, ...props }, ref) => {
    var _a;
    const internalRef = useRef(null);
    const memoizedId = useSSRSafeId('navigation-drawer', id);
    const { closeNavigationDrawer, setNavigationDrawerId } = useNavigationDrawer();
    const isNavigationDrawerOpen = useIsNavigationDrawerOpen();
    const combinedRef = useCombinedRef(internalRef, ref);
    const { handleNavigationDrawerTrackingData } = useTrackingData(DataLayerCategory.MENU);
    useEffect(() => {
        setNavigationDrawerId(memoizedId);
    }, [memoizedId, setNavigationDrawerId]);
    useModal({
        open: isNavigationDrawerOpen,
        closeHandler: closeNavigationDrawer,
    });
    useFocusTrap(internalRef, isNavigationDrawerOpen);
    const { isKeyboardControlled } = useInteractionMode();
    const isReducedMotion = useReducedMotion();
    const isDisabledMotion = isKeyboardControlled || isReducedMotion;
    const mobileMode = useMobileMode();
    const { name } = useMediaQuery();
    const deviceType = DeviceTypes[name || DeviceTypes.desktop];
    const initialColumnsOnScreen = COLUMNS_ON_SCREEN[deviceType];
    const [navigationState, navigationDispatch] = useReducer(navigationReducer, {
        mobileMode,
        deviceType,
        usefulColumns: 0,
        columnsOnScreen: initialColumnsOnScreen,
        delayedTimer: 0,
        navigationIndex: mobileMode ? -1 : 1 - initialColumnsOnScreen,
        columns: [],
        aiming: false,
    });
    const { columns, aiming, usefulColumns, navigationIndex, columnsOnScreen } = navigationState;
    // flatten grouped links
    const links = useMemo(() => flattenGroupsIntoLinks(groups), [groups]);
    // Add unique key based on hierarchy so the dev doesn't have to
    const linksWithKeys = useMemo(() => addKeysToNavigationDrawer(links, 'nav'), [links]);
    const [currentFocusPath, setCurrentFocusPath] = useState(((_a = linksWithKeys === null || linksWithKeys === void 0 ? void 0 : linksWithKeys[0]) === null || _a === void 0 ? void 0 : _a.path) || 'drawer-path');
    const columnWidth = 100 / columnsOnScreen; // in VW or %
    const setAiming = useCallback((aiming) => {
        navigationDispatch({ type: 'setAiming', aiming });
    }, []);
    useEffect(() => {
        navigationDispatch({
            type: 'setDisplayMode',
            mobileMode,
            deviceType,
        });
    }, [mobileMode, deviceType]);
    const setColumn = useCallback((index, content = null) => {
        navigationDispatch({
            type: 'setColumn',
            index,
            column: content,
        });
    }, []);
    useEffect(() => {
        var _a;
        navigationDispatch({ type: 'clearColumns' });
        setCurrentFocusPath(((_a = linksWithKeys === null || linksWithKeys === void 0 ? void 0 : linksWithKeys[0]) === null || _a === void 0 ? void 0 : _a.path) || 'nav.0');
        if (linksWithKeys && isNavigationDrawerOpen) {
            setColumn(0, {
                index: 0,
                path: 'nav',
                links: linksWithKeys,
                header,
            });
        }
        return () => {
            setCurrentFocusPath(null);
            setColumn(0, null);
        };
    }, [setColumn, header, linksWithKeys, isNavigationDrawerOpen, isDisabledMotion]);
    const handleTransitionEnd = useCallback(() => {
        onAnimationEnded === null || onAnimationEnded === void 0 ? void 0 : onAnimationEnded(isNavigationDrawerOpen);
    }, [onAnimationEnded, isNavigationDrawerOpen]);
    const needsBackButton = isNavigationDrawerOpen && navigationIndex > 0;
    const handleBack = useCallback(() => {
        setColumn(columns.length - 1);
        handleNavigationDrawerTrackingData(DataLayerLabel.MENU_BACK, DataLayerAction.MENU_CLICK);
    }, [columns.length, setColumn, handleNavigationDrawerTrackingData]);
    const activePaths = useMemo(() => columns.map((column) => column.path), [columns]);
    const [headerHeight, setHeaderHeight] = useState(0);
    const handleFocusChange = useCallback((index) => {
        setCurrentFocusPath((currentFocusPath) => `${getPreviousFocusPath(currentFocusPath)}.${index}`);
    }, []);
    const handleKeyDown = useCallback((e) => {
        switch (e.key) {
            case 'Escape':
                e.stopPropagation();
                if (usefulColumns > 1) {
                    navigationDispatch({ type: 'removeColumn' });
                    setCurrentFocusPath(getPreviousFocusPath(currentFocusPath));
                }
                else {
                    closeNavigationDrawer();
                }
                break;
        }
    }, [closeNavigationDrawer, usefulColumns, currentFocusPath]);
    const handleListKeyDown = useCallback((e) => {
        var _a, _b, _c, _d, _e;
        const links = (_a = columns[columns.length - 1]) === null || _a === void 0 ? void 0 : _a.links;
        let currentLinkIndex = (links === null || links === void 0 ? void 0 : links.findIndex((link) => link.path === currentFocusPath)) || -1;
        currentLinkIndex = currentLinkIndex > -1 ? currentLinkIndex : 0;
        if (!links) {
            return;
        }
        if (!isDisabledMotion) {
            preventCancelableDefault(e);
            return;
        }
        switch (e.key) {
            case 'ArrowLeft':
            case 'Tab': {
                if (e.key === 'Tab' && (!e.shiftKey || usefulColumns === 1)) {
                    break;
                }
                preventCancelableDefault(e);
                // close a column or all
                if (usefulColumns > 1) {
                    navigationDispatch({ type: 'removeColumn' });
                    setCurrentFocusPath(getPreviousFocusPath(currentFocusPath));
                }
                else {
                    closeNavigationDrawer();
                    setCurrentFocusPath(((_b = linksWithKeys === null || linksWithKeys === void 0 ? void 0 : linksWithKeys[0]) === null || _b === void 0 ? void 0 : _b.path) || 'nav.0');
                }
                handleTransitionEnd();
                break;
            }
            case 'ArrowRight': {
                preventCancelableDefault(e);
                const hasLinks = !!((_d = (_c = links[currentLinkIndex]) === null || _c === void 0 ? void 0 : _c.links) === null || _d === void 0 ? void 0 : _d.length);
                if (hasLinks) {
                    setColumn(columns.length, {
                        links: ((_e = links === null || links === void 0 ? void 0 : links[currentLinkIndex]) === null || _e === void 0 ? void 0 : _e.links) || [],
                        path: currentFocusPath,
                    });
                    setCurrentFocusPath(currentFocusPath + '.0');
                }
                handleTransitionEnd();
                break;
            }
        }
    }, [
        columns,
        isDisabledMotion,
        currentFocusPath,
        usefulColumns,
        handleTransitionEnd,
        closeNavigationDrawer,
        linksWithKeys,
        setColumn,
    ]);
    const transition = createTransition({
        disabled: isDisabledMotion,
        ...Transitions.largeSurface,
    });
    const backgroundWidth = round(usefulColumns * columnWidth, 1);
    const backgroundX = useDirectionStyle(Math.min(backgroundWidth, 100)) + 'vw';
    const columnWrapperX = useDirectionStyle(-round(navigationIndex * columnWidth, 1)) + 'vw';
    const directionStyle = {
        open: useDirectionStyle(needsBackButton ? 0 : 100),
        closed: useDirectionStyle(-300),
    };
    const backButtonX = isNavigationDrawerOpen
        ? `${directionStyle.open}vw`
        : `${directionStyle.closed}%`;
    const closeButtonClick = closeButtonProps === null || closeButtonProps === void 0 ? void 0 : closeButtonProps.onClick;
    const handleCloseClick = useCallback((e) => {
        closeNavigationDrawer();
        closeButtonClick === null || closeButtonClick === void 0 ? void 0 : closeButtonClick(e);
        handleNavigationDrawerTrackingData(DataLayerLabel.MENU_CLOSED, DataLayerAction.MENU_CLICK);
    }, [closeButtonClick, closeNavigationDrawer, handleNavigationDrawerTrackingData]);
    if (groups === undefined || (groups === null || groups === void 0 ? void 0 : groups.length) === 0) {
        warnOnce('NavigationDrawer', 'Provide at least 1 link to be able to navigate the site. (groups=', groups === null || groups === void 0 ? void 0 : groups.toString(), ')');
        return null;
    }
    // this is needed to ensure there are no render issues that you need to click twice on the close button for example
    const backgroundAnimation = isDisabledMotion
        ? { x: backgroundX, duration: 0 }
        : { x: backgroundX };
    const content = (React.createElement(Wrapper, { id: memoizedId, ...props, ref: combinedRef, onKeyDown: handleKeyDown, ...aria({ hidden: !isNavigationDrawerOpen }) },
        React.createElement(Background, { as: motion.div,
            initial: false,
            animate: backgroundAnimation,
            transition,
            onAnimationComplete: handleTransitionEnd },
            React.createElement(ColumnWrapper, { ...(isDisabledMotion
                    ? {
                        as: 'div',
                        style: {
                            transform: `translateX(${columnWrapperX}) translateZ(0px)`,
                        },
                    }
                    : {
                        as: motion.div,
                        initial: false,
                        animate: {
                            x: columnWrapperX,
                        },
                        transition,
                    }) },
                React.createElement(AnimatePresence, null, columns.map((column) => {
                    const { index, links, header } = column;
                    if (!links) {
                        return null;
                    }
                    return (React.createElement(Column, { key: `${column.path}-${index}`, index: index, columnsOnScreen: columnsOnScreen, aim: !mobileMode && !!(columns[index + 1] && columns[index + 1].links), aiming: aiming, setAiming: setAiming, header: header, headerHeight: headerHeight, setHeaderHeight: setHeaderHeight, initial: { opacity: 0 }, transition: transition, animate: { opacity: 1 }, exit: { opacity: 0, pointerEvents: 'none' } },
                        React.createElement(Inner, null,
                            React.createElement(NavigationDrawerList, { index: index, active: index === navigationIndex + columnsOnScreen - 1, links: links, setColumn: setColumn, activePaths: activePaths, onKeyDown: handleListKeyDown, onFocusChange: handleFocusChange }))));
                }))),
            React.createElement(CloseButton, { ...closeButtonProps, tabIndex: isNavigationDrawerOpen ? 0 : -1, onClick: handleCloseClick, icon: Icons.close, size: Sizes.large, variant: IconButtonVariants.transparent })),
        React.createElement(BackButton, { ...backButtonProps, onClick: handleBack, visible: needsBackButton, icon: Icons.arrowLeft, size: Sizes.large, tabIndex: needsBackButton ? 0 : -1, initial: false, animate: {
                opacity: needsBackButton ? 1 : 0,
                x: backButtonX,
            }, transition: Transitions.reduced, ...aria({ hidden: !needsBackButton }) })));
    return (React.createElement(React.Fragment, null,
        !isNavigationDrawerOpen && React.createElement(SSRNavigation, { groups: linksWithKeys }),
        React.createElement(Portal, null,
            React.createElement(Backdrop, { visible: isNavigationDrawerOpen, onClose: closeNavigationDrawer, variant: BackdropVariants.default }),
            React.createElement(ThemeProvider, { theme: internal ? Themes.dark : Themes.light }, content))));
});
NavigationDrawer.displayName = 'NavigationDrawer';
export default NavigationDrawer;
const Wrapper = styled.div `
  position: fixed;
  height: 100%;
  width: 0;
  z-index: ${LayoutIndexes.modal};
`;
const ColumnWrapper = styled.div `
  transform: translateX(0vw) translateZ(0px);
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  height: 100%;
  width: 100%;
  z-index: 3;
`;
const aimingDebug = false;
const columnReducer = (state, action) => {
    switch (action.type) {
        case 'alreadyAiming':
            return {
                ...state,
                alreadyAiming: action.alreadyAiming,
            };
        case 'updateLoc':
            if (state.alreadyAiming) {
                return state;
            }
            let newPosition = state.position;
            if (aimingDebug) {
                const widthPx = window.innerWidth / 3;
                const height = window.innerHeight;
                const offset = action.index * widthPx;
                const newWidth = widthPx - (action.loc.x - offset);
                newPosition = {
                    width: newWidth,
                    height,
                    points: `0,${action.loc.y}  ${newWidth},0  ${newWidth},${height}`,
                };
            }
            return {
                ...state,
                loc: action.loc,
                prevLoc: state.loc,
                position: newPosition,
            };
        case 'reset':
            return {
                ...state,
                loc: null,
                prevLoc: null,
                position: null,
            };
    }
};
const Column = memo(({ children, aim, aiming, index, setAiming, columnsOnScreen, header, headerHeight, setHeaderHeight, ...props }) => {
    const columnRef = useRef(null);
    const [columnState, columnDispatch] = useReducer(columnReducer, {
        loc: null,
        prevLoc: null,
        position: null,
        alreadyAiming: false,
    });
    useEffect(() => {
        if (columnState.loc && columnState.prevLoc) {
            const widthPx = window.innerWidth / columnsOnScreen;
            const height = window.innerHeight;
            const upperRight = {
                x: widthPx,
                y: 0,
            };
            const lowerRight = {
                x: widthPx,
                y: height,
            };
            /**
             * This checks if the current mouse position and the new mouse position
             * moves in a triangle to the next open column. If the mouse moves in a
             * wider angle then the 2 given points (upperRight and lowerRight) it will
             * mark that as "not aiming" and trigger anything that is below. If it does
             * it will mark the polygon as active which goes the mouseover to be on
             * the polygon instead of any link that might be below that.
             *
             * You can remove the "false" on the <polygon> styled components to see
             * what is drawn and used for aiming of the mouse
             */
            const decreasingDelta = aimingDelta(columnState.loc, upperRight);
            const increasingDelta = aimingDelta(columnState.loc, lowerRight);
            const prevDecreasingDelta = aimingDelta(columnState.prevLoc, upperRight);
            const prevIncreasingDelta = aimingDelta(columnState.prevLoc, lowerRight);
            setAiming(decreasingDelta < prevDecreasingDelta && increasingDelta > prevIncreasingDelta);
        }
        return () => {
            setAiming(false);
        };
    }, [setAiming, columnState.loc, columnState.prevLoc, columnsOnScreen]);
    const alreadyAiming = !aimingDebug && aiming;
    useEffect(() => {
        columnDispatch({
            type: 'alreadyAiming',
            alreadyAiming,
        });
        return () => {
            columnDispatch({
                type: 'reset',
            });
        };
    }, [alreadyAiming]);
    const handleMouseMove = useCallback((e) => {
        if (!aim) {
            return;
        }
        columnDispatch({
            type: 'updateLoc',
            loc: {
                x: e.nativeEvent.clientX,
                y: e.nativeEvent.clientY,
            },
            index,
        });
    }, [index, aim]);
    const headerRef = useCallback((node) => {
        if (node && headerHeight !== node.offsetHeight) {
            setHeaderHeight(node.offsetHeight);
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [header, headerHeight, setHeaderHeight]);
    useEffect(() => {
        const throttled = setTimeout(() => setAiming(false), 250);
        return () => {
            throttled && clearTimeout(throttled);
        };
    }, [aiming, setAiming]);
    const scrollRef = useRef(null);
    return (React.createElement(StyledColumn, { ...props, ref: columnRef, index: index, onMouseMove: handleMouseMove, columnsOnScreen: columnsOnScreen },
        React.createElement(HeaderWrapper, { ref: headerRef, initial: false, height: index > 0 ? headerHeight : null, animate: { opacity: !index ? 1 : 0 }, transition: Transitions.default }, header),
        React.createElement(ColumnInner, { ref: scrollRef, tabIndex: -1 }, children),
        aimingDebug && aim && columnState.position && columnState.position.width ? (React.createElement(AimSVG, { aiming: aiming, height: columnState.position.height, width: `${columnState.position.width}px`, role: "img" },
            React.createElement("polygon", { points: columnState.position.points }))) : null));
});
Column.displayName = 'Column';
const AimSVG = styled.svg `
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;
  pointer-events: none;

  > polygon {
    fill: ${({ aiming, theme }) => aimingDebug && aiming
    ? highlight(theme.navigationDrawer.backgroundColor, 0.2)
    : 'transparent'};
  }
`;
const ColumnInner = styled(ScrollView) `
  position: relative;
  flex: 1;

  scrollbar-width: none;
  overflow-style: none;
  -ms-overflow-style: none;
  overscroll-behavior: contain;

  ::-webkit-scrollbar {
    width: 0;
    background: transparent;
  }
`;
const StyledColumn = styled(motion.div, shouldNotForwardProps(false, 'index', 'columnsOnScreen')) `
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: ${({ index, columnsOnScreen }) => index * (100 / columnsOnScreen)}vw;
  height: 100%;
  width: ${({ columnsOnScreen }) => 100 / columnsOnScreen}vw;
  z-index: ${({ index }) => index + 1};
  background: ${({ theme }) => theme.navigationDrawer.backgroundColor};
  overflow: hidden;
  display: flex;
  flex-direction: column;

  /* Required to align NavigationDrawer header with the Polestar header */
  ${responsiveStyles({
    paddingBlockStart: {
        mobile: Spacings.small,
        tablet: Spacings.medium,
        desktop: Spacings.large,
    },
})}
`;
const Inner = styled.div `
  display: flex;
  flex: 1;
  flex-direction: column;

  ${responsiveStyles({
    padding: {
        mobile: `0 ${Spacings.large}`,
        tablet: `0 ${Spacings.large} 0 ${Spacings.xLarge}`,
        desktop: `0 ${Spacings.xLarge} 0 ${Spacings.xxxLarge}`,
        desktopWide: `0 ${Spacings.xLarge} 0 ${Spacings.xxxxxLarge}`,
    },
})}
`;
const Background = styled.div(({ theme }) => css `
    position: fixed;
    inset-block-start: 0;
    height: 100%;
    z-index: 2;
    overflow: hidden;
    inset-inline-start: -100vw;
    width: 100vw;
    background: ${theme.navigationDrawer.backgroundColor};
  `);
const BackButton = styled(motion(IconButton))(({ visible }) => css `
    position: absolute;
    z-index: 20;
    pointer-events: ${visible ? 'auto' : 'none'};
    max-width: none;
    margin-inline-start: -${createCustomSpacing(1 / 4)};
    ${responsiveStyles({
    insetBlockStart: {
        mobile: Spacings.none,
        tablet: Spacings.xSmall,
        desktop: Spacings.medium,
    },
    insetInlineStart: {
        mobile: Spacings.medium,
        tablet: Spacings.large,
        desktop: Spacings.xxxLarge,
    },
    marginBlockStart: {
        mobile: Spacings.small,
    },
})}
  `);
const CloseButton = styled(motion(IconButton)) `
  pointer-events: auto;
  position: absolute;
  z-index: 20;
  max-width: none;
  &::before,
  &::after {
    content: none;
  }
  ${responsiveStyles({
    insetBlockStart: {
        mobile: Spacings.none,
        tablet: Spacings.xSmall,
        desktop: Spacings.medium,
    },
    insetInlineEnd: {
        mobile: Spacings.medium,
        tablet: Spacings.large,
        desktop: Spacings.xLarge,
        desktopWide: createCustomSpacing(2.25),
    },
    /* reset back button left */
    insetInlineStart: {
        mobile: 'auto',
        tablet: 'auto',
        desktop: 'auto',
    },
    marginInlineEnd: {
        mobile: '0',
        tablet: `-${Spacings.xSmall}`,
        desktop: `-${Spacings.medium}`,
        desktopWide: `-${createCustomSpacing(1.25)}`,
    },
    /* Required to align vertically with both the NavigationDrawer and the Polestar header */
    marginBlockStart: {
        mobile: Spacings.small,
        desktop: createCustomSpacing(1.25),
    },
})}
  /* Aligns the close button 'X' vertically to the menu arrows below */
  [dir='rtl'] & {
    ${responsiveStyles({
    marginInlineEnd: {
        mobile: Spacings.xxSmall,
        desktop: Spacings.medium,
        desktopWide: createCustomSpacing(2.75),
    },
})}
`;
const HeaderWrapper = styled(motion.div, shouldNotForwardProps(false, 'height'))(({ height }) => css(css `
      display: flex;
      align-items: center;
      min-height: ${Spacings.large};

      ${responsiveStyles({
    margin: {
        mobile: `${Spacings.small} ${Spacings.large} ${Spacings.small} ${Spacings.large}`,
        tablet: `${createCustomSpacing(20 / 16)} ${Spacings.xLarge} ${createCustomSpacing(20 / 16)} ${Spacings.xLarge}`,
        desktop: `${Spacings.large} ${Spacings.xxLarge} ${Spacings.large} ${Spacings.xxxLarge}`,
        desktopWide: `${Spacings.large} ${Spacings.xxLarge} ${Spacings.large} ${Spacings.xxxxxLarge}`,
    },
})},
    `, !!height &&
    css `
        height: ${height}px;
      `));
